<template>
  <div class="slow-sale-warning">
    <div class="stat-panel">
      <div class="stat-panel__head">
        <div class="stat-panel__title">汽运出库智能滞销煤预警</div>
        <el-button size="small" icon="el-icon-refresh" :loading="statisticsLoading" @click="loadStatistics">
          刷新统计
        </el-button>
      </div>
      <div v-loading="statisticsLoading" class="stat-grid">
        <div v-for="item in statisticsCards" :key="item.key" class="stat-item" :class="`stat-item--${item.type}`">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <small>{{ item.desc }}</small>
        </div>
      </div>
    </div>

    <div class="filter-panel">
      <el-form :model="query" label-width="86px" size="small">
        <el-row :gutter="12">
          <el-col :xs="24" :sm="12" :md="5">
            <el-form-item label="客户">
              <el-select
                v-model="query.custNo"
                clearable
                filterable
                remote
                reserve-keyword
                placeholder="客户"
                :remote-method="loadCustomers"
                :loading="customerLoading"
                @visible-change="handleCustomerVisible"
                style="width: 100%"
              >
                <el-option v-for="item in customerOptions" :key="item.id" :label="item.deptName" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="5">
            <el-form-item label="煤种">
              <el-select
                v-model="query.varNo"
                clearable
                filterable
                remote
                reserve-keyword
                placeholder="煤种"
                :remote-method="loadCargoes"
                :loading="cargoLoading"
                @visible-change="handleCargoVisible"
                style="width: 100%"
              >
                <el-option v-for="item in cargoOptions" :key="item.varNo" :label="item.varName" :value="item.varNo" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="4">
            <el-form-item label="预警等级">
              <el-select v-model="query.warningLevel" clearable placeholder="全部" style="width: 100%">
                <el-option label="正常" :value="0" />
                <el-option label="预警" :value="1" />
                <el-option label="严重" :value="2" />
                <el-option label="危急" :value="3" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="4">
            <el-form-item label="处理状态">
              <el-select v-model="query.warningStatus" clearable placeholder="全部" style="width: 100%">
                <el-option label="未处理" :value="0" />
                <el-option label="已解除" :value="1" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6" class="query-actions">
            <el-button size="small" icon="el-icon-refresh" @click="resetQuery">重置</el-button>
            <el-button size="small" type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">
              查询
            </el-button>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <div class="table-panel">
      <div class="table-toolbar">
        <div class="table-toolbar__title">预警列表</div>
        <el-button
          size="small"
          type="primary"
          plain
          icon="el-icon-download"
          :loading="exportLoading"
          @click="handleExport"
        >
          导出
        </el-button>
      </div>
      <el-table
        v-loading="loading"
        :data="records"
        border
        stripe
        row-key="id"
        empty-text="暂无滞销煤预警数据"
        :row-class-name="getRowClassName"
      >
        <el-table-column type="index" label="序号" width="70" align="center" />
        <el-table-column prop="custName" label="客户" min-width="150" show-overflow-tooltip />
        <el-table-column prop="varName" label="物资名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="varNo" label="煤种编码" width="120" show-overflow-tooltip />
        <el-table-column prop="locationName" label="仓库/库位" min-width="170" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.warehouseName || "-" }} / {{ row.locationName || "-" }}</template>
        </el-table-column>
        <el-table-column label="当前库存(吨)" width="130" align="right">
          <template slot-scope="{ row }">{{ formatWeight(row.currentInventory) }}</template>
        </el-table-column>
        <el-table-column label="日均出库(吨/日)" width="145" align="right">
          <template slot-scope="{ row }">{{ formatWeight(row.dailyTransport) }}</template>
        </el-table-column>
        <el-table-column label="窗口出库量(吨)" width="145" align="right">
          <template slot-scope="{ row }">{{ formatWeight(row.transport30d) }}</template>
        </el-table-column>
        <el-table-column prop="inventoryDays" label="库存天数" width="100" align="right">
          <template slot-scope="{ row }">{{ row.inventoryDays || row.inventoryDays === 0 ? `${row.inventoryDays} 天` : "-" }}</template>
        </el-table-column>
        <el-table-column prop="warningLevel" label="预警等级" width="110" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="getWarningTagType(row.warningLevel)" size="mini">
              {{ getWarningText(row.warningLevel) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="warningStatus" label="处理状态" width="110" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.warningStatus === 1 ? 'success' : 'warning'" size="mini">
              {{ row.warningStatus === 1 ? "已解除" : "未处理" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="warnTime" label="预警时间" width="160" />
        <el-table-column prop="suggestion" label="建议处理" min-width="260" show-overflow-tooltip />
        <el-table-column label="操作" width="90" fixed="right" align="center">
          <template slot-scope="{ row }">
            <el-button
              v-if="row.warningStatus !== 1"
              type="text"
              size="small"
              icon="el-icon-check"
              @click="handleRelease(row)"
            >
              解除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="page.currentPage"
        :page-size="page.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="page.total"
        @current-change="handleCurrentChange"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog
      title="解除滞销煤预警"
      :visible.sync="releaseDialogVisible"
      width="520px"
      append-to-body
      @closed="resetReleaseForm"
    >
      <el-form ref="releaseForm" :model="releaseForm" :rules="releaseRules" label-width="90px" size="small">
        <el-form-item label="解除人" prop="releaseName">
          <el-input v-model.trim="releaseForm.releaseName" maxlength="50" clearable placeholder="请输入解除人" />
        </el-form-item>
        <el-form-item label="解除原因" prop="releaseReason">
          <el-input
            v-model.trim="releaseForm.releaseReason"
            type="textarea"
            :rows="4"
            maxlength="500"
            show-word-limit
            placeholder="请输入解除原因"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="releaseDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="releaseLoading" @click="submitRelease">确认解除</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  exportExcel,
  getCargoOptions,
  getCustomerOptions,
  getList,
  getSlowSaleConfig,
  getStatistics,
  releaseAlarm,
} from "./api";
import { downloadXls } from "@/util/util";
import { unwrapDictList } from "@/util/dict-list";

export default {
  name: "IntelligentCoalWarn",
  data() {
    return {
      query: this.defaultQuery(),
      loading: false,
      statisticsLoading: false,
      exportLoading: false,
      releaseLoading: false,
      customerLoading: false,
      cargoLoading: false,
      statistics: {},
      slowSaleConfig: this.defaultSlowSaleConfig(),
      records: [],
      customerOptions: [],
      cargoOptions: [],
      releaseDialogVisible: false,
      currentReleaseRow: null,
      releaseForm: this.defaultReleaseForm(),
      releaseRules: {
        releaseName: [{ required: true, message: "请输入解除人", trigger: "blur" }],
        releaseReason: [{ required: true, message: "请输入解除原因", trigger: "blur" }],
      },
      page: {
        currentPage: 1,
        pageSize: 10,
        total: 0,
      },
    };
  },
  computed: {
    statisticsCards() {
      const data = this.statistics || {};
      const config = this.slowSaleConfig || this.defaultSlowSaleConfig();
      return [
        {
          key: "total",
          label: "总预警数",
          value: this.formatCount(data.total),
          type: "total",
          desc: `库存≥${this.formatConfigNumber(config.minStockWeight)}吨参与统计`,
        },
        {
          key: "normal",
          label: "正常",
          value: this.formatCount(data.normal),
          type: "normal",
          desc: `库存天数 < ${this.formatConfigNumber(config.stockDaysWarn)}天`,
        },
        {
          key: "warning",
          label: "预警",
          value: this.formatCount(data.warning),
          type: "warning",
          desc: `库存天数 ≥ ${this.formatConfigNumber(config.stockDaysWarn)}天`,
        },
        {
          key: "serious",
          label: "严重",
          value: this.formatCount(data.serious),
          type: "serious",
          desc: `库存天数 ≥ ${this.formatConfigNumber(config.stockDaysSerious)}天`,
        },
        {
          key: "critical",
          label: "危急",
          value: this.formatCount(data.critical),
          type: "critical",
          desc: `库存天数 ≥ ${this.formatConfigNumber(config.stockDaysCritical)}天`,
        },
        {
          key: "unresolved",
          label: "未处理",
          value: this.formatCount(data.unresolved),
          type: "unresolved",
          desc: "处理状态为未处理",
        },
        {
          key: "released",
          label: "已解除",
          value: this.formatCount(data.released),
          type: "released",
          desc: "处理状态为已解除",
        },
      ];
    },
  },
  created() {
    this.loadSlowSaleConfig();
    this.handleSearch();
  },
  methods: {
    defaultQuery() {
      return {
        custNo: "",
        varNo: "",
        warningLevel: "",
        warningStatus: "",
      };
    },
    defaultSlowSaleConfig() {
      return {
        stockDaysWarn: 15,
        stockDaysSerious: 30,
        stockDaysCritical: 45,
        minStockWeight: 1,
      };
    },
    defaultReleaseForm() {
      return {
        releaseName: "",
        releaseReason: "",
      };
    },
    async handleSearch() {
      this.page.currentPage = 1;
      await Promise.all([this.loadData(), this.loadStatistics()]);
    },
    async loadData() {
      this.loading = true;
      try {
        const res = await getList(this.buildParams(true));
        const data = (res.data && res.data.data) || {};
        this.records = data.records || [];
        this.page.total = Number(data.total || 0);
      } catch (error) {
        this.records = [];
        this.page.total = 0;
        this.$message.error(this.getErrorMessage(error, "智能滞销煤预警查询失败"));
      } finally {
        this.loading = false;
      }
    },
    async loadStatistics() {
      this.statisticsLoading = true;
      try {
        const res = await getStatistics(this.buildParams(false));
        this.statistics = (res.data && res.data.data) || {};
      } catch (error) {
        this.statistics = {};
        this.$message.error(this.getErrorMessage(error, "智能滞销煤预警统计失败"));
      } finally {
        this.statisticsLoading = false;
      }
    },
    async loadSlowSaleConfig() {
      try {
        const res = await getSlowSaleConfig();
        this.slowSaleConfig = Object.assign(
          {},
          this.defaultSlowSaleConfig(),
          (res.data && res.data.data) || {}
        );
      } catch (error) {
        this.slowSaleConfig = this.defaultSlowSaleConfig();
      }
    },
    resetQuery() {
      this.query = this.defaultQuery();
      this.handleSearch();
    },
    buildParams(withPage) {
      const params = {
        businessType: 2,
        warnType: 3,
      };
      if (withPage) {
        params.page = this.page.currentPage;
        params.size = this.page.pageSize;
      }
      Object.keys(this.query).forEach((key) => {
        if (this.query[key] !== "" && this.query[key] !== null && this.query[key] !== undefined) {
          params[key] = this.query[key];
        }
      });
      return params;
    },
    async loadCustomers(keyword) {
      this.customerLoading = true;
      try {
        const res = await getCustomerOptions(keyword);
        this.customerOptions = this.normalizeOptionList(res);
      } finally {
        this.customerLoading = false;
      }
    },
    async loadCargoes(keyword) {
      this.cargoLoading = true;
      try {
        const res = await getCargoOptions(keyword);
        this.cargoOptions = this.normalizeOptionList(res);
      } finally {
        this.cargoLoading = false;
      }
    },
    handleCustomerVisible(visible) {
      if (visible && !this.customerOptions.length) {
        this.loadCustomers("");
      }
    },
    handleCargoVisible(visible) {
      if (visible && !this.cargoOptions.length) {
        this.loadCargoes("");
      }
    },
    normalizeOptionList(res) {
      return unwrapDictList(res);
    },
    handleCurrentChange(currentPage) {
      this.page.currentPage = currentPage;
      this.loadData();
    },
    handleSizeChange(pageSize) {
      this.page.pageSize = pageSize;
      this.page.currentPage = 1;
      this.loadData();
    },
    handleRelease(row) {
      this.currentReleaseRow = row;
      this.releaseForm = this.defaultReleaseForm();
      this.releaseDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.releaseForm) {
          this.$refs.releaseForm.clearValidate();
        }
      });
    },
    submitRelease() {
      this.$refs.releaseForm.validate(async (valid) => {
        if (!valid || !this.currentReleaseRow) {
          return;
        }
        this.releaseLoading = true;
        try {
          await releaseAlarm({
            id: this.currentReleaseRow.id,
            releaseName: this.releaseForm.releaseName,
            releaseReason: this.releaseForm.releaseReason,
          });
          this.$message.success("解除成功");
          this.releaseDialogVisible = false;
          await Promise.all([this.loadData(), this.loadStatistics()]);
        } catch (error) {
          this.$message.error(this.getErrorMessage(error, "解除失败"));
        } finally {
          this.releaseLoading = false;
        }
      });
    },
    resetReleaseForm() {
      this.currentReleaseRow = null;
      this.releaseForm = this.defaultReleaseForm();
      if (this.$refs.releaseForm) {
        this.$refs.releaseForm.clearValidate();
      }
    },
    async handleExport() {
      const total = this.page.total || 0;
      if (total < 1) {
        this.$message.warning("无数据可导出");
        return;
      }
      const maxRows = 10000;
      if (total > maxRows) {
        this.$message.warning(`当前最多导出 ${maxRows} 条，请缩小筛选范围后重试`);
        return;
      }
      this.exportLoading = true;
      try {
        const res = await exportExcel(this.buildParams(false));
        downloadXls(res.data, `汽运出库滞销煤预警_${Date.now()}.xlsx`);
        this.$message.success("导出成功");
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, "导出失败"));
      } finally {
        this.exportLoading = false;
      }
    },
    getWarningText(level) {
      const map = {
        0: "正常",
        1: "预警",
        2: "严重",
        3: "危急",
      };
      return map[level] || "未知";
    },
    getWarningTagType(level) {
      const map = {
        0: "success",
        1: "warning",
        2: "danger",
        3: "danger",
      };
      return map[level] || "info";
    },
    getRowClassName({ row }) {
      if (row.warningLevel === 3) {
        return "row-critical";
      }
      if (row.warningLevel === 2) {
        return "row-serious";
      }
      return "";
    },
    formatWeight(value) {
      const num = Number(value || 0);
      return num.toFixed(2);
    },
    formatCount(value) {
      const num = Number(value || 0);
      return Number.isFinite(num) ? num : 0;
    },
    formatConfigNumber(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) {
        return "-";
      }
      return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/\.?0+$/, "");
    },
    getErrorMessage(error, fallback) {
      return (
        (error && error.response && error.response.data && (error.response.data.msg || error.response.data.message)) ||
        (error && error.message) ||
        fallback
      );
    },
  },
};
</script>

<style scoped>
.slow-sale-warning {
  padding: 12px;
  background: #f5f7fa;
}

.stat-panel,
.filter-panel,
.table-panel {
  padding: 14px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
}

.stat-panel__head,
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.stat-panel__title,
.table-toolbar__title {
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: 12px;
  min-height: 74px;
}

.stat-item {
  padding: 10px 12px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fafafa;
}

.stat-item span {
  display: block;
  color: #909399;
  font-size: 12px;
}

.stat-item strong {
  display: block;
  margin-top: 6px;
  color: #303133;
  font-size: 20px;
}

.stat-item small {
  display: block;
  min-height: 16px;
  margin-top: 5px;
  color: #a8abb2;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
}

.stat-item--total strong,
.stat-item--unresolved strong {
  color: #409eff;
}

.stat-item--normal strong,
.stat-item--released strong {
  color: #67c23a;
}

.stat-item--warning strong {
  color: #e6a23c;
}

.stat-item--serious strong,
.stat-item--critical strong {
  color: #f56c6c;
}

.filter-panel,
.table-panel {
  margin-top: 12px;
}

.query-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 32px;
}

.pager {
  margin-top: 12px;
  text-align: right;
}

::v-deep .row-critical td {
  background: #fff1f0;
}

::v-deep .row-serious td {
  background: #fff7e6;
}
</style>
